import { z } from 'zod';
import { BRANCH_CODES } from '../types/branches.js';
import { SUPPLY_TYPES } from '../types/enums.js';

export const UpdateStockTargetSchema = z.object({
  min_qty: z.number().int().nonnegative().optional(),
  // 0 is allowed: target_qty = 0 disables the SKU from reorder/transfer views
  // (they gate on `target_qty > 0`). DB only enforces target_qty >= min_qty.
  target_qty: z.number().int().nonnegative().optional(),
  safety_days: z.number().int().nonnegative().optional(),
  supply_type: z.enum(SUPPLY_TYPES).optional(),
  primary_supplier_id: z.number().int().positive().nullable().optional(),
  auto_calc: z.boolean().optional(),
}).refine((d) => Object.keys(d).length > 0, {
  message: 'At least one field required',
});
export type UpdateStockTargetInput = z.infer<typeof UpdateStockTargetSchema>;

export const ApplySupplierAcrossBranchesSchema = z.object({
  supplier_id: z.number().int().positive().nullable(),
});
export type ApplySupplierAcrossBranchesInput = z.infer<
  typeof ApplySupplierAcrossBranchesSchema
>;

// ----------------------------------------------------------------------------
// Bulk import — upload a parsed CSV worth of (sku, branch) targets at once.
// Server upserts into ops.stock_targets; the existing trigger logs each
// change to ops.stock_targets_history automatically.
// ----------------------------------------------------------------------------
export const ImportTargetsRowSchema = z.object({
  sku: z.string().min(1),
  branch: z.enum(BRANCH_CODES),
  // SKU-level metadata for admin-only SKUs not in public.tire_catalog.
  // Server upserts these into ops.sku_meta; tire_catalog SKUs ignore them
  // since the catalog is the canonical source.
  brand: z.string().max(200).optional(),
  model: z.string().max(200).optional(),
  size: z.string().max(50).optional(),
  min_qty: z.number().int().nonnegative().optional(),
  target_qty: z.number().int().positive().optional(),
  safety_days: z.number().int().nonnegative().optional(),
  supply_type: z.enum(SUPPLY_TYPES).optional(),
  primary_supplier_id: z.number().int().positive().nullable().optional(),
  auto_calc: z.boolean().optional(),
});
export type ImportTargetsRow = z.infer<typeof ImportTargetsRowSchema>;

export const ImportTargetsSchema = z.object({
  rows: z.array(ImportTargetsRowSchema).min(1),
  /** When true, server validates + returns plan but does NOT write. */
  dry_run: z.boolean().default(true),
});
export type ImportTargetsInput = z.infer<typeof ImportTargetsSchema>;
